import { useState, useEffect } from 'react';
import './GameSetupForm.css';

export interface Team {
  id: string;
  name: string;
  color: string;
}

export interface GameSetup {
  gameName: string;
  numberOfSprints: number;
  teams: Team[];
}

interface GameSetupFormProps {
  onClose: () => void;
}

const COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', 
  '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2',
  '#F8B739', '#52B788', '#E63946', '#A8DADC'
];

export default function GameSetupForm({ onClose }: GameSetupFormProps) {
  const [gameName, setGameName] = useState('');
  const [numberOfSprints, setNumberOfSprints] = useState(5);
  const [teams, setTeams] = useState<Team[]>([]);
  const [copiedMessage, setCopiedMessage] = useState('');

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('gameSetup');
    if (saved) {
      try {
        const setup: GameSetup = JSON.parse(saved);
        setGameName(setup.gameName || '');
        setNumberOfSprints(setup.numberOfSprints || 5);
        setTeams(setup.teams || []);
      } catch (e) {
        console.error('Failed to parse saved game setup', e);
      }
    }
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => {
    const setup: GameSetup = {
      gameName,
      numberOfSprints,
      teams,
    };
    localStorage.setItem('gameSetup', JSON.stringify(setup));
  }, [gameName, numberOfSprints, teams]);

  const addTeam = () => {
    const newTeam: Team = {
      id: Date.now().toString(),
      name: '',
      color: COLORS[teams.length % COLORS.length],
    };
    setTeams([...teams, newTeam]);
  };

  const deleteTeam = (id: string) => {
    setTeams(teams.filter(team => team.id !== id));
  };

  const updateTeam = (id: string, field: 'name' | 'color', value: string) => {
    setTeams(teams.map(team => 
      team.id === id ? { ...team, [field]: value } : team
    ));
  };

  const generateTeamLink = (team: Team) => {
    const params = new URLSearchParams({
      gameName: gameName,
      numberOfSprints: numberOfSprints.toString(),
      teamName: team.name,
      teamColor: team.color,
    });
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  };

  const copyTeamLink = async (team: Team) => {
    const link = generateTeamLink(team);
    try {
      await navigator.clipboard.writeText(link);
      setCopiedMessage(`Link copied for ${team.name || 'team'}!`);
      setTimeout(() => setCopiedMessage(''), 2000);
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };

  const copyAllLinks = async () => {
    const allLinks = teams
      .filter(team => team.name) // Only include teams with names
      .map(team => `${team.name}: ${generateTeamLink(team)}`)
      .join('\n\n');
    
    if (allLinks) {
      try {
        await navigator.clipboard.writeText(allLinks);
        setCopiedMessage('All links copied!');
        setTimeout(() => setCopiedMessage(''), 2000);
      } catch (err) {
        console.error('Failed to copy all links', err);
      }
    }
  };

  const clearForm = () => {
    setGameName('');
    setNumberOfSprints(5);
    setTeams([]);
    localStorage.removeItem('gameSetup');
  };

  return (
    <div className="popup-overlay">
      <div className="game-setup-modal">
        <h2>⚙️ Set Up Game</h2>
        
        <div className="form-section">
          <label htmlFor="gameName">Game Name:</label>
          <input
            id="gameName"
            type="text"
            value={gameName}
            onChange={(e) => setGameName(e.target.value)}
            placeholder="Enter game name (e.g., your company name)"
            className="form-input"
          />
        </div>

        <div className="form-section">
          <label htmlFor="numberOfSprints">Number of Sprints (5-10):</label>
          <input
            id="numberOfSprints"
            type="number"
            min="5"
            max="10"
            value={numberOfSprints}
            onChange={(e) => setNumberOfSprints(Math.min(10, Math.max(5, parseInt(e.target.value) || 5)))}
            className="form-input"
          />
        </div>

        <div className="form-section">
          <div className="section-header">
            <label>Teams:</label>
            <button onClick={addTeam} className="add-team-button">+ Add Team</button>
          </div>
          
          <div className="teams-list">
            {teams.map((team) => (
              <div key={team.id} className="team-row">
                <input
                  type="text"
                  value={team.name}
                  onChange={(e) => updateTeam(team.id, 'name', e.target.value)}
                  placeholder="Team name"
                  className="team-name-input"
                />
                <input
                  type="color"
                  value={team.color}
                  onChange={(e) => updateTeam(team.id, 'color', e.target.value)}
                  className="team-color-input"
                />
                <button 
                  onClick={() => copyTeamLink(team)} 
                  className="copy-link-button"
                  disabled={!gameName || !team.name}
                  title={!gameName || !team.name ? 'Enter game name and team name first' : 'Copy link for this team'}
                >
                  📋 Copy Link
                </button>
                <button 
                  onClick={() => deleteTeam(team.id)} 
                  className="delete-team-button"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </div>

        {copiedMessage && (
          <div className="copied-message">{copiedMessage}</div>
        )}

        <div className="form-actions">
          <button 
            onClick={copyAllLinks} 
            className="action-button secondary"
            disabled={!gameName || teams.filter(t => t.name).length === 0}
          >
            📋 Copy All Links
          </button>
          <button onClick={clearForm} className="action-button secondary">
            🗑️ Clear Form
          </button>
          <button onClick={onClose} className="action-button primary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
